<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    transition="slide-y-transition"
    :close-on-content-click="false"
    dense
    data-testid="status-filter-menu"
  >
    <!-- Activator -->
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        v-bind="attrs"
        v-on="on"
        data-testid="status-filter-menu__toggle-button"
      >
        <img
          src="/img/icons/FilterIcon.svg"
          alt="Menu Filter Icon"
          :class="{ 'filter-icon--active': hasActiveFilters }"
          class="filter-icon"
        />
      </v-btn>
    </template>

    <!-- Card -->
    <v-card class="status-filter-card">
      <!-- Header -->
      <v-card-title class="status-filter-header">
        <span class="header-title">Status Filter</span>
        <v-btn icon small @click="menuOpen = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Table -->
      <v-simple-table class="ma-2 status-simple-table">
        <thead class="tableHeaderBg">
          <tr>
            <th class="checkbox-col">
              <v-checkbox
                hide-details
                class="status-checkbox"
                :input-value="isAllSelected"
                @change="toggleSelectAll"
                data-testid="status-filter-menu__select-all"
              />
            </th>
            <th class="status-info-col">Select All</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="option in filterOptions"
            :key="option.value"
            class="status-row"
          >
            <td class="checkbox-col">
              <!-- Checkbox -->
              <v-checkbox
                hide-details
                class="status-checkbox"
                :input-value="isFilterSelected(option.value)"
                @change="toggleFilter(option.value)"
              />
            </td>

            <!-- Status info -->
            <td class="status-info-col">
              <div class="status-info">
                <StatusIndicator 
                  :variant="option.variant" 
                  :custom-label="option.label"
                  size="sm"
                  style="width: auto;"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Footer -->
      <v-divider></v-divider>
      <v-card-actions class="status-footer">
        <v-spacer></v-spacer>
        <v-btn class="primary" @click="applyFilters" data-testid="status-filter-menu__apply-btn">
          Apply
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import StatusIndicator from './StatusIndicator.vue';
import {
  COMMENTARY_STAGE_VARIANT_MAP,
  COMMENTARY_STAGE_LABEL_MAP,
  STATUS_VARIANTS,
  STATUS_CONFIG
} from '../utils/statusConstants';

export default {
  name: 'StatusFilterMenu',

  components: {
    StatusIndicator
  },

  props: {
    selectedfilter: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      menuOpen: false,
      allFilterValues: [0, 1, -1, 5, 6, -2, 7, 4, 3, 9],
      pendingFilters: []
    };
  },

  watch: {
    menuOpen(isOpen) {
      if (isOpen) {
        this.pendingFilters = (this.selectedfilter || []).slice();
      }
    }
  },

  computed: {
    isAllSelected() {
      return this.allFilterValues.every(val => 
        this.pendingFilters.some(f => String(f) === String(val))
      );
    },

    /**
     * Check if filters are active (not all selected)
     * Returns true when user has filtered out some statuses
     */
    hasActiveFilters() {
      if (!this.selectedfilter || this.selectedfilter.length === 0) return false;
      // Active when not all values are selected
      return this.selectedfilter.length < this.allFilterValues.length;
    },

    filterOptions() {
      return this.allFilterValues.map(stage => {
        const variant =
          COMMENTARY_STAGE_VARIANT_MAP[String(stage)] ||
          STATUS_VARIANTS.UNKNOWN;

        const labelConfig = COMMENTARY_STAGE_LABEL_MAP[String(stage)];
        // Use fallback directly to ensure concise labels (e.g. "Approved")
        const label = labelConfig
          ? labelConfig.fallback
          : 'Unknown Status';

        const config = STATUS_CONFIG[variant];
        const color = config ? config.color : '#999';

        return {
          value: stage,
          label,
          variant,
          color
        };
      });
    }
  },

  methods: {
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.pendingFilters = [];
      } else {
        this.pendingFilters = this.allFilterValues.slice();
      }
    },

    getLabel(key, fallback) {
      if (this.$nova && this.$nova.labelsName) {
        return this.$nova.labelsName('cycle_page', key, fallback);
      }
      return fallback;
    },

    isFilterSelected(value) {
      return this.pendingFilters.some(f => String(f) === String(value));
    },

    toggleFilter(value) {
      const index = this.pendingFilters.findIndex(f => String(f) === String(value));
      if (index > -1) {
        this.pendingFilters.splice(index, 1);
      } else {
        this.pendingFilters.push(value);
      }
    },

    applyFilters() {
      this.$emit('update:selected-filter', this.pendingFilters.slice());
      this.menuOpen = false;
    }
  }
};
</script>

<style scoped>
/* Card */
.status-filter-card {
  width: 320px;
  border-radius: 10px;
  overflow: hidden;
}

/* Header */
.status-filter-header {
  background-color: #e1e1e1;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

/* Table */
.status-simple-table{
  height: 400px;
  overflow-y: auto;
}

/* Rows */
.status-row {
  border-bottom: 1px solid #e0e0e0;
}

.status-row:hover {
  background-color: #f7f7f7;
}

/* Checkbox */
.status-checkbox {
  margin: 0;
  padding: 0;
}

/* Status info */
.status-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Footer */
.status-footer {
  padding: 12px;
}

.apply-btn {
  background-color: #0f2a33;
  color: #fff;
  border-radius: 20px;
  padding: 6px 22px;
  text-transform: none;
}

/* Filter icon */
.filter-icon {
  width: 18px;
  height: 18px;
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.filter-icon--active {
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(15%) sepia(20%) saturate(1000%) hue-rotate(160deg) brightness(90%) contrast(95%);
}
</style>
